import { useEffect } from "react";
import { useSelector } from "react-redux";

import { web } from "@kikoff/proto/src/protos";
import { format } from "@kikoff/utils/src/string";

import { getUtilizationTier } from "@component/dashboard/utilization_meter";
import { InfoTooltip } from "@component/tooltips/tooltips";
import { useOverlaysController } from "@src/overlay";

import styles from "./product_breakdown.module.scss";

interface ProductBreakdownProps {
  preview: web.public_.ICheckoutPreviewResponse;
}

export default function ProductBreakdown({ preview }: ProductBreakdownProps) {
  const overlays = useOverlaysController();
  const creditLine = useSelector((state) => state.creditLine.account);

  const { product } = preview.order.orderItems[0];

  const additionalMonths =
    product.repaymentTerm - (creditLine?.loanDurationMonths || 0);

  const eligibleForCreditLimitIncrease =
    preview && preview.newCreditLimitCents !== preview.creditLimitCents;

  const creditLimitValue = eligibleForCreditLimitIncrease
    ? preview.newCreditLimitCents
    : preview.creditLimitCents;

  const percentage =
    preview && !Number.isNaN(creditLimitValue)
      ? ((preview.newCreditBalanceCents * 100) / creditLimitValue).toFixed(1)
      : 0;

  useEffect(() => {
    if (+percentage >= 30) {
      overlays.push("shopping/utilization_warning", {});
    }
  }, [percentage]);

  const tier = getUtilizationTier(percentage);

  const formattedMinimumPayment = format.money(
    preview.creditLineAgreement.minimumPaymentAmountCents
  );
  const minimumPaymentPercent = `${preview.creditLineAgreement.minimumPaymentAmountPercents}%`;

  return (
    <div className={styles["product-breakdown"]}>
      {creditLine ? (
        <div>
          <header>Extend payment history</header>
          <div>
            {additionalMonths}
            <div className={styles.label}>months</div>
          </div>
        </div>
      ) : (
        <div>
          <header>Payment history</header>
          <div>
            {product.repaymentTerm}
            <div className={styles.label}>months</div>
          </div>
        </div>
      )}
      <div>
        <header>
          <InfoTooltip
            prompt="Est. next payment"
            className={styles.tooltip}
            leftOffset="100px"
            title
            message={
              <>
                Your estimated next payment is what your next minimum payment
                will be if you purchase this item. It is the higher of{" "}
                {formattedMinimumPayment} or {minimumPaymentPercent} of your
                statement balance.
              </>
            }
          />
        </header>
        <div>
          {preview &&
            format.money(preview.paymentSchedule.monthlyPaymentCents, {
              includeCents: true,
            })}
          <div className={styles.label} />
        </div>
      </div>
      <div>
        <header>
          <InfoTooltip
            prompt={
              creditLine ? "New credit utilization" : "Credit utilization"
            }
            className={styles.tooltip}
            title
            leftOffset="220px"
            message={
              <>
                This is will be your credit utilization based on your balance if
                you purchase this item. Keep your credit utilization below 15%
                to have a positive impact on your credit.
              </>
            }
          />
        </header>
        <div style={{ color: tier.color }}>
          {preview && percentage}%
          <div className={styles.label}>{tier.label}</div>
        </div>
      </div>
    </div>
  );
}
